import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isPortfolio, Site } from "../../sites/sites.module";
import { selectTender } from "../../tender_form/tenderForm.selector";
import GenericTable from "../../../common/components/GenericTable";
import { ColModel } from "../../../common/components/GenericTableRow";
import { ReactCountryFlag } from "react-country-flag";
import { useNavigate } from "react-router-dom";
import { PATH } from "../../../router-path";
import { PrimaryButton } from "../../../common/components/CustomButton";

const rowModel: ColModel<Site>[] = [
	{
		id: "TPR id",
		noTooltip: false,
		header: () => "TPR id",
		xs: 1,
		align: "left",
		accessor: (model: Site, _index: number) => model.asset_id,
	},
	{
		id: "name",
		noTooltip: false,
		header: () => "Name",
		xs: 2,
		align: "left",
		accessor: (model: Site, _index: number) =>
			model.specific_name || model.name,
	},
	{
		id: "country",
		header: () => "Country",
		xs: 0.5,
		accessor: (m: Site) => (
			<>
				{"country" in m && (
					<>
						<ReactCountryFlag
							countryCode={m.country ?? ""}
							svg
							title={m.country ?? ""}
						/>

						{m.country}
					</>
				)}
			</>
		),
	},
	{
		id: "technology",
		header: () => "Technology",
		xs: 1,
		accessor: (m: Site) => "techno" in m && m?.techno?.name,
	},
	{
		id: "#pricings",
		header: () => "Pricings",
		xs: 1,
		accessor: (m: Site) => {
			return (
				<Typography
					variant="subtitle2"
					sx={{
						...(!m.count_attached_pricings && {
							color: "red",
						}),
					}}
				>
					{m.count_attached_pricings}
				</Typography>
			);
		},
	},
];

export function SiteComparisonSelectionModal() {
	const navigate = useNavigate();
	const [isOpen, setIsOpen] = useState(false);

	const tender = useSelector(selectTender);

	const [sites, setSites] = useState<Site[]>([]);
	const [selected, setSelected] = useState<Site[]>([]);

	useEffect(() => {
		setSites(tender?.sites || []);
	}, [tender]);

	const onCompare = useCallback(() => {
		const siteFilters = selected.map((site) => ({
			id: site.asset_id,
			name: site.specific_name,
		}));
		const search = new URLSearchParams({
			filters: JSON.stringify({ site: siteFilters }),
		});
		navigate(PATH.PRICINGS + "?" + search.toString());
	}, [navigate, selected]);

	const onOpen = useCallback(() => {
		setSelected([]);
		setIsOpen(true);
	}, []);

	return (
		<>
			<PrimaryButton
				text={"Compare pricings"}
				type="button"
				onClick={onOpen}
				color="info"
				sx={{
					marginRight: "16px",
					width: 150,
					backgroundColor: "#C5CED5",
					color: "#171D21",
				}}
			></PrimaryButton>
			<Dialog open={isOpen} fullWidth={true} maxWidth={"md"}>
				<DialogTitle>Site comparison</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<Typography>
							Please select which sites you would like to compare
						</Typography>
						<GenericTable<Site, Site>
							keyExtractor={(data: Site) => {
								if (isPortfolio(data)) {
									return `portfolio_${data.id}`;
								}
								return `site_${data.id}`;
							}}
							virtualScrollable={true}
							itemName="Sites"
							updatePage={() => {}}
							page={0}
							model={sites}
							rowModel={rowModel}
							isSelectable={true}
							loading={false}
							selection={setSelected}
						/>
					</DialogContentText>
					<DialogActions>
						<Button onClick={() => setIsOpen(false)}>Cancel</Button>
						<Button
							data-testid="compare-action"
							variant="contained"
							onClick={onCompare}
							disabled={selected.length === 0}
						>
							Compare
						</Button>
					</DialogActions>
				</DialogContent>
			</Dialog>
		</>
	);
}
